/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Film Grain & Noise Node
   Procedural reproducible seeded noise with monochrome/color modes.
   Zero random flicker on static graphs.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER, GLSL_NOISE_UTILS } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';

const GRAIN_SHADER = `${GLSL_HEADER}
${GLSL_NOISE_UTILS}

uniform sampler2D u_image;
uniform vec2 u_resolution;
uniform float u_amount;
uniform float u_scale;
uniform float u_seed;
uniform bool u_colorMode;

void main() {
  vec4 src = texture(u_image, v_uv);
  vec2 noiseCoord = (v_uv * u_resolution) / max(u_scale, 0.5);

  if (!u_colorMode) {
    // Monochrome grain
    float noise = hash21(noiseCoord, u_seed);
    // Center noise around 0 [-0.5 .. 0.5]
    float grain = (noise - 0.5) * u_amount;
    vec3 result = clamp(src.rgb + vec3(grain), 0.0, 1.0);
    fragColor = vec4(result, src.a);
  } else {
    // Chromatic / color noise
    vec3 noise = hash23(noiseCoord, u_seed);
    vec3 grain = (noise - vec3(0.5)) * u_amount;
    vec3 result = clamp(src.rgb + grain, 0.0, 1.0);
    fragColor = vec4(result, src.a);
  }
}
`;

export const grainNode: NodeDefinition = {
  type: 'filter.grain',
  category: 'Filter',
  label: 'Film Grain',
  description: 'Simulates analog film stock grain with reproducible procedural noise',
  keywords: ['grain', 'noise', 'film', 'analog', 'texture', 'subtle', 'iso'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Grain', type: 'image' },
  ],
  params: [
    {
      id: 'amount',
      name: 'Grain Amount',
      type: 'slider',
      defaultValue: 25,
      min: 0,
      max: 100,
      step: 1,
      unit: '%',
    },
    {
      id: 'scale',
      name: 'Grain Size',
      type: 'slider',
      defaultValue: 1.5,
      min: 0.5,
      max: 8.0,
      step: 0.1,
      unit: 'px',
    },
    {
      id: 'seed',
      name: 'Random Seed',
      type: 'number',
      defaultValue: 1337,
      min: 0,
      max: 99999,
      step: 1,
    },
    {
      id: 'colorMode',
      name: 'Color Noise',
      type: 'boolean',
      defaultValue: false,
      uiHint: 'toggle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: GRAIN_SHADER,
    getUniforms: (inputs, params, dimensions) => ({
      u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
      u_resolution: [dimensions.width, dimensions.height],
      u_amount: (Number(params.amount) || 25) / 100,
      u_scale: Number(params.scale) || 1.5,
      u_seed: Number(params.seed) || 1337,
      u_colorMode: Boolean(params.colorMode),
    }),
  }),
};
